/**
 * @file Switch atom — boolean toggle control.
 * @description Structured atom over the vendored Gluestack family; the only
 * sanctioned import path for app code (RFC §3).
 *
 * **It now carries a colour, and that is the whole of this file's substance.**
 * Until SE-7550 this was a bare re-export, so the track fell through to React
 * Native Web's default — a teal-green that appears nowhere in the design
 * system. Figma binds the Starter switch (`120:1516`) to `accent/primary`, and
 * every other switch in the app inherited the same wrong default.
 *
 * The colour cannot be a className. React Native's `trackColor` is a style
 * PROP taking a value, and the app has no token→value bridge — `ThemeProvider`
 * exposes the mode and no palette. So the value is named here, in the one layer
 * permitted to name raw shades (RFC §3: "Raw shades are atom-internal only").
 * A feature file doing this would be the `no-raw-palette` violation the rule
 * exists to catch, and the repo's one precedent — hex literals in
 * `StatusSettingsView` — is exactly that debt.
 *
 * `useColorScheme` rather than a context read: the stateless-atom rule bans
 * `useState`, `useEffect`, `useReducer`, `useContext`, `useQuery` and
 * `useMutation`, and this is none of them. The values track
 * `--color-accent-primary` in `gluestack-ui-provider/config.ts` and must move
 * with it.
 * @module components/atoms/Switch
 */
import React from "react";
import { useColorScheme } from "react-native";

import { Switch as UISwitch } from "@/components/ui/switch";

/** `--color-accent-primary`, per theme. Atom-internal by RFC §3. */
const ACCENT_TRACK = {
  light: "rgb(0, 122, 255)",
  dark: "rgb(10, 132, 255)",
} as const;

/**
 * The OFF track, which had no colour at all.
 *
 * `trackColor` was set with only a `true` entry, so the off state fell through
 * to transparent — measured `rgba(0, 0, 0, 0)` on a 40x14 track. With the knob
 * now white in both states, an off switch rendered as a white circle floating
 * on the panel with no track under it: no affordance, and no way to tell it
 * from a loading dot. It went unnoticed until the Starter default became
 * `false`, which is what put a switch on screen in the off state at all.
 *
 * `--color-outline-300` in light (`211 211 211`) and `--color-outline-600` in
 * dark (`115 116 116`): the inactive-control grey at each theme's own weight.
 * Taken from the outline family rather than a background shade because this is
 * a control's edge state, not a surface.
 */
const OFF_TRACK = {
  light: "rgb(211, 211, 211)",
  dark: "rgb(115, 116, 116)",
} as const;

/**
 * The knob, in BOTH states.
 *
 * Setting the track alone left the thumb on React Native's default, which is
 * the same teal-green this file removed from the track — so the Starter switch
 * still rendered a green knob on a blue track, which is what Raniel saw
 * (2026-08-31). The frame (`120:1516`) draws a plain white knob.
 *
 * `--color-background-0` in light, and white in dark too: the knob sits ON the
 * accent track in both themes, so it is the track it must contrast with, not
 * the page. A themed knob would go dark-on-blue in dark mode and disappear.
 */
const KNOB = "rgb(255, 255, 255)";

/**
 * The ON knob on WEB, which is a second prop entirely.
 *
 * react-native-web splits the thumb in two: `thumbColor` is the OFF knob and
 * `activeThumbColor` is the ON one, whose default is Material teal `#009688`.
 * React Native core has only `thumbColor`. So setting `thumbColor` alone fixes
 * native and leaves the web switch with a green knob on an accent track —
 * measured `rgb(0, 150, 136)` on the Starter toggle, which is that default
 * exactly.
 *
 * Spread as an untyped record because `activeThumbColor` is not on RN core's
 * `SwitchProps`; it is inert on native, where `thumbColor` already covers both
 * states.
 */
const WEB_ACTIVE_THUMB: Record<string, unknown> = { activeThumbColor: KNOB };

/** Props for the Switch atom — the vendored control's, unchanged. */
type SwitchProps = React.ComponentProps<typeof UISwitch>;

/**
 * A toggle whose ON track is the design system's accent.
 *
 * `trackColor` is still overridable — a caller with a bound variable saying
 * otherwise should say so — but the default is the accent rather than whatever
 * the platform picks.
 * @param root0 - Switch props, forwarded to the vendored control.
 * @param root0.trackColor - Optional override for the track colours.
 * @param root0.thumbColor - Optional override for the knob colour.
 * @returns The switch element.
 */
const Switch = ({ trackColor, thumbColor, ...props }: SwitchProps) => {
  const scheme = useColorScheme();

  return (
    <UISwitch
      {...WEB_ACTIVE_THUMB}
      {...props}
      trackColor={
        trackColor ?? {
          false: OFF_TRACK[scheme === "dark" ? "dark" : "light"],
          true: ACCENT_TRACK[scheme === "dark" ? "dark" : "light"],
        }
      }
      thumbColor={thumbColor ?? KNOB}
    />
  );
};

Switch.displayName = "Switch";

export { Switch };
